import React, { useCallback } from "react";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * Reusable Filters Bar (header + body) for notifications
 *
 * Props:
 *  - loading: boolean
 *  - uiStatusFilter: "all" | "unread" | "read"
 *  - setUiStatusFilter: (next: string) => void
 *  - uiTypeFilter: string[]
 *  - setUiTypeFilter: (next: string[] | ((prev)=>string[])) => void
 *  - typeOptions: Array<{value:string,label:string}>
 *  - onReset: () => void
 *  - onApply: () => void
 *  - onCompose: () => void
 * Optional:
 *  - title: string
 *  - subtitle: string
 *  - showCompose: boolean (default true)
 *  - showStatusFilter: boolean (default true)
 */
export default function NotificationFiltersBar({
                                                   loading,
                                                   uiStatusFilter,
                                                   setUiStatusFilter,
                                                   uiTypeFilter,
                                                   setUiTypeFilter,
                                                   typeOptions = [],
                                                   onReset,
                                                   onApply,
                                                   onCompose,
                                                   title,
                                                   subtitle,
                                                   showCompose = true,
                                                   showStatusFilter = true,
                                               }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const resolvedTitle =
        title ?? t("notificationFiltersBar.header.title");
    const resolvedSubtitle =
        subtitle ?? t("notificationFiltersBar.header.subtitle");

    const onChangeStatus = useCallback(
        (e) => setUiStatusFilter?.(e.target.value),
        [setUiStatusFilter]
    );

    const onToggleType = useCallback(
        (value) => {
            const v = String(value);
            setUiTypeFilter?.((prev) => {
                const safe = Array.isArray(prev) ? prev : [];
                const isActive = safe.includes(v);
                return isActive ? [] : [v];
            });
        },
        [setUiTypeFilter]
    );

    const hasTypes = Array.isArray(typeOptions) && typeOptions.length > 0;

    return (
        <div className="kbola-notif-filter-wrap">
            <div className="kbola-notif-filter-head">
                <div className="kbola-notif-filter-head-left">
                    <span className="kbola-notif-filter-badge">
                        <i className="fas fa-filter" />
                    </span>

                    <div className="kbola-notif-filter-head-text">
                        <div className="kbola-notif-filter-title">{resolvedTitle}</div>
                        <div className="kbola-notif-filter-subtitle">{resolvedSubtitle}</div>
                    </div>
                </div>

                <div className="kbola-notif-filter-head-right">
                    {showCompose ? (
                        <button
                            type="button"
                            className="btn btn-primary kbola-filter-btn kbola-compose-trigger"
                            onClick={onCompose}
                            disabled={loading}
                            title={t("notificationFiltersBar.tooltips.compose")}
                        >
                            <i className="fas fa-bullhorn mr-2" />
                            {t("notificationFiltersBar.actions.compose")}
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className="btn btn-light kbola-filter-btn"
                        onClick={onReset}
                        disabled={loading}
                        title={t("notificationFiltersBar.tooltips.reset")}
                    >
                        <i className="fas fa-undo mr-2" />
                        {t("notificationFiltersBar.actions.reset")}
                    </button>

                    <button
                        type="button"
                        className="btn btn-dark kbola-filter-btn"
                        onClick={onApply}
                        disabled={loading}
                        title={t("notificationFiltersBar.tooltips.apply")}
                    >
                        <i className="fas fa-check mr-2" />
                        {t("notificationFiltersBar.actions.apply")}
                    </button>
                </div>
            </div>

            <div className="kbola-notif-filter-body">
                {showStatusFilter ? (
                    <div className="kbola-filter-group">
                        <div className="kbola-filter-label">
                            {t("notificationFiltersBar.fields.status")}
                        </div>

                        <select
                            className="form-control kbola-filter-select"
                            value={uiStatusFilter}
                            onChange={onChangeStatus}
                            aria-label={t("notificationFiltersBar.fields.filterByStatus")}
                            disabled={loading}
                        >
                            <option value="all">
                                {t("notificationFiltersBar.statusOptions.all")}
                            </option>
                            <option value="unread">
                                {t("notificationFiltersBar.statusOptions.unread")}
                            </option>
                            <option value="read">
                                {t("notificationFiltersBar.statusOptions.read")}
                            </option>
                        </select>
                    </div>
                ) : null}

                <div className="kbola-filter-group kbola-filter-types">
                    <div className="kbola-filter-label">
                        {t("notificationFiltersBar.fields.typeSingleSelect")}
                    </div>

                    {!hasTypes ? (
                        <div className="kbola-filter-empty">
                            {t("notificationFiltersBar.empty.noTypesAvailable")}
                        </div>
                    ) : (
                        <div
                            className="kbola-type-chips"
                            role="group"
                            aria-label={t("notificationFiltersBar.fields.filterByType")}
                        >
                            {typeOptions.map((typeOption) => {
                                const active = (uiTypeFilter || []).includes(typeOption.value);

                                return (
                                    <button
                                        key={typeOption.value}
                                        type="button"
                                        className={`kbola-type-chip ${active ? "is-active" : ""}`}
                                        onClick={() => onToggleType(typeOption.value)}
                                        title={typeOption.label}
                                        aria-pressed={active}
                                        disabled={loading}
                                    >
                                        {typeOption.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
        .kbola-notif-filter-wrap{
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          background: #fff;
          box-shadow: 0 18px 44px rgba(0,0,0,0.10);
          margin-bottom: 12px;
        }

        .kbola-notif-filter-head{
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 12px 14px;
          background: linear-gradient(
            135deg,
            ${alphaFromHex(colors.primary, 0.50)},
            ${alphaFromHex(colors.primary, 0.00)}
          );
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        .kbola-notif-filter-head-left{
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .kbola-notif-filter-badge{
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 999px;
          background: ${colors.primary};
          border: 1px solid rgba(0,0,0,0.10);
          color: ${colors.textOnPrimary};
          flex: 0 0 34px;
        }

        .kbola-notif-filter-head-text{
          min-width: 0;
        }

        .kbola-notif-filter-title{
          font-size: 14px;
          font-weight: 900;
          color: #14151a;
          line-height: 1.1;
        }

        .kbola-notif-filter-subtitle{
          margin-top: 2px;
          font-size: 12.5px;
          font-weight: 700;
          color: rgba(20,21,26,0.55);
        }

        .kbola-notif-filter-head-right{
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 0 0 auto;
        }

        .kbola-notif-filter-body{
          padding: 12px 14px;
          display: flex;
          gap: 14px;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        .kbola-filter-group{
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 180px;
        }

        .kbola-filter-types{
          min-width: 320px;
          flex: 1 1 420px;
        }

        .kbola-filter-label{
          font-size: 12px;
          font-weight: 900;
          letter-spacing: 0.02em;
          color: rgba(20,21,26,0.70);
        }

        .kbola-filter-select{
          border-radius: 14px !important;
          font-weight: 800;
          border: 1px solid rgba(0,0,0,0.10) !important;
        }

        .kbola-type-chips{
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .kbola-type-chip{
          border: 1px solid rgba(0,0,0,0.10);
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.04);
          color: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.85);
          border-radius: 999px;
          padding: 8px 10px;
          font-weight: 950;
          font-size: 12px;
          cursor: pointer;
          transition: transform 120ms ease, background 120ms ease;
        }

        .kbola-type-chip:hover{
          background: rgba(var(--kbs-text-on-primary-rgb, 20,21,26), 0.07);
        }

        .kbola-type-chip:active{
          transform: translateY(1px);
        }

        .kbola-type-chip.is-active{
          background: ${colors.secondary};
          color: ${colors.textOnSecondary};
          border-color: rgba(0,0,0,0.12);
        }

        .kbola-type-chip:disabled{
          opacity: 0.75;
          cursor: not-allowed;
        }

        .kbola-filter-empty{
          font-size: 12px;
          font-weight: 800;
          color: rgba(220,53,69,0.80);
          padding: 8px 10px;
          border: 1px dashed rgba(220,53,69,0.25);
          border-radius: 12px;
          background: rgba(220,53,69,0.04);
        }

        .kbola-filter-btn{
          border-radius: 14px !important;
          font-weight: 900 !important;
          padding: 10px 12px !important;
        }

        .kbola-compose-trigger{
          background: ${colors.primary} !important;
          border: 1px solid rgba(0,0,0,0.10) !important;
          color: ${colors.textOnPrimary} !important;
        }

        @media (max-width: 768px){
          .kbola-notif-filter-head{
            align-items: stretch;
            flex-direction: column;
          }

          .kbola-notif-filter-head-right{
            justify-content: flex-end;
            width: 100%;
            flex-wrap: wrap;
          }

          .kbola-notif-filter-body{
            align-items: stretch;
          }

          .kbola-filter-group{
            min-width: 0;
            width: 100%;
          }
        }
      `}</style>
        </div>
    );
}
